import { AttachMoney, Close, Edit, Save, TrendingUp } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useApi } from '@shared/services/api';
import { log } from '@shared/utils/log';
import React, { useContext, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';

interface HourlyRateBannerProps {
  onUpdate?: () => void;
}

export const HourlyRateBanner = ({ onUpdate }: HourlyRateBannerProps): React.ReactElement | null => {
  const { user, updateUser } = useContext(UserContext);
  const api = useApi();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>(user?.hourlyRate?.toString() ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  if (user?.hourlyRateBannerDismissedAt) {
    return null;
  }

  const hasRate = user?.hourlyRate !== undefined && user?.hourlyRate !== null;

  const handleSave = async (): Promise<void> => {
    if (!user?.id) return;

    const hourlyRate = editValue ? Number.parseFloat(editValue) : undefined;

    if (editValue && (Number.isNaN(hourlyRate) || (hourlyRate ?? 0) < 0)) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.put('user', {
        id: user.id,
        hourlyRate,
      });

      if (!response.error) {
        updateUser({ ...user, hourlyRate });
        setIsEditing(false);
        onUpdate?.();
      }
    } catch (error) {
      log.error('Error updating hourly rate:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    setEditValue(user?.hourlyRate?.toString() ?? '');
    setIsEditing(false);
  };

  const handleDismiss = async (): Promise<void> => {
    if (!user?.id) return;
    const dismissedAt = new Date();
    setIsDismissing(true);
    try {
      const response = await api.put('user', {
        id: user.id,
        hourlyRateBannerDismissedAt: dismissedAt,
      });
      if (!response.error) {
        updateUser({ ...user, hourlyRateBannerDismissedAt: dismissedAt });
      }
    } catch (error) {
      log.error('Error dismissing hourly rate banner:', error);
    } finally {
      setIsDismissing(false);
    }
  };

  const dismissButton = (
    <Tooltip title="Dismiss">
      <span>
        <IconButton
          size="small"
          onClick={handleDismiss}
          disabled={isDismissing}
          className="rate-banner__dismiss"
          aria-label="Dismiss consulting rate banner"
          data-testid="hourly-rate-banner-dismiss"
        >
          <Close fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  const editForm = (
    <div className="rate-banner__form" data-testid="hourly-rate-form">
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        type="number"
        size="small"
        placeholder="0"
        className="rate-banner__input"
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
          endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
        }}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') handleCancel();
        }}
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving || !editValue}
        className="rate-banner__save"
        data-testid="hourly-rate-save"
      >
        <Save fontSize="small" />
        <span>{hasRate ? 'Update' : 'Save rate'}</span>
      </button>
      <button
        type="button"
        onClick={handleCancel}
        disabled={isSaving}
        className="rate-banner__cancel"
        aria-label="Cancel"
      >
        <Close fontSize="small" />
      </button>
    </div>
  );

  if (hasRate) {
    return (
      <section className="rate-banner rate-banner--set" aria-label="Consulting rate" data-testid="hourly-rate-banner">
        <span className="rate-banner__icon" aria-hidden="true">
          <AttachMoney />
        </span>

        <div className="rate-banner__body">
          <p className="rate-banner__eyebrow">Your consulting rate</p>
          <p className="rate-banner__hint">Visible when network buyers request a consultation with you.</p>
        </div>

        <div className="rate-banner__action">
          {isEditing ? (
            editForm
          ) : (
            <>
              <p className="rate-banner__price" data-testid="hourly-rate-value">
                <span className="rate-banner__currency">$</span>
                <span className="rate-banner__amount">{user!.hourlyRate}</span>
                <span className="rate-banner__unit">/hr</span>
              </p>
              <Tooltip title="Edit rate">
                <IconButton
                  size="small"
                  onClick={() => setIsEditing(true)}
                  className="rate-banner__edit"
                  aria-label="Edit hourly rate"
                  data-testid="hourly-rate-edit"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>

        {dismissButton}
      </section>
    );
  }

  return (
    <section
      className="rate-banner rate-banner--empty"
      aria-label="Set your consulting rate"
      data-testid="hourly-rate-banner"
    >
      <div className="rate-banner__hero">
        <span className="rate-banner__icon rate-banner__icon--large" aria-hidden="true">
          <AttachMoney />
        </span>
        <div className="rate-banner__body">
          <p className="rate-banner__eyebrow">Consulting opportunity</p>
          <h3 className="rate-banner__headline">Earn from your expertise</h3>
          <p className="rate-banner__pitch">
            Set your hourly rate to start receiving paid consultation requests from buyers in the Kandir Network.
          </p>
        </div>
      </div>

      <div className="rate-banner__action rate-banner__action--stacked">
        {isEditing ? (
          editForm
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="rate-banner__cta"
            data-testid="hourly-rate-set-cta"
          >
            Set my rate
          </button>
        )}
        <p className="rate-banner__footnote">
          <TrendingUp className="rate-banner__footnote-icon" />
          Account Executives typically charge $200–$500/hour
        </p>
      </div>

      {dismissButton}
    </section>
  );
};
